import { listBrowserVoices } from '../features/tts';
import { bundledProfiles } from '../profiles/defaults';
import { bundledLanguages, type VoiceEngine, type WidgetPosition } from '../types';
import type { FeatureSettings, InclusiveState, Profile } from '../types';
import { widgetCss } from './styles';
import { applyTheme } from './theme';

const copy: Record<string, Record<string, string>> = {
    en: {
        brand: 'UGACCESS',
        title: 'Accessibility',
        close: 'Close accessibility panel',
        open: 'Open accessibility options',
        launcher: 'Accessibility',
        shortcut: 'Alt+Shift+A',
        profiles: 'Profiles',
        display: 'Display',
        textSize: 'Text size',
        decreaseText: 'Decrease text size',
        increaseText: 'Increase text size',
        vision: 'Vision',
        reading: 'Reading',
        motion: 'Motion and focus',
        language: 'Language',
        voiceReader: 'Voice reader',
        voiceEngine: 'Voice engine',
        browserVoice: 'Browser voice',
        voiceAuto: 'Auto (Sunbird, then cloud, then browser)',
        voiceSunbird: 'Sunbird',
        voiceAzure: 'Azure Speech',
        voiceGoogle: 'Google Cloud',
        voiceBrowser: 'Browser voices',
        voiceMatchLanguage: 'Match page language',
        readingSpeed: 'Reading speed',
        decreaseSpeed: 'Slower reading',
        increaseSpeed: 'Faster reading',
        readPage: 'Read page',
        stopReading: 'Stop reading',
        reset: 'Reset',
        highContrast: 'High contrast',
        grayscale: 'Grayscale',
        invertColors: 'Invert colors',
        highlightLinks: 'Highlight links',
        largeCursor: 'Large cursor',
        reduceMotion: 'Reduce motion',
        textSpacing: 'Text spacing',
        readingMode: 'Reading mode',
        keyboardNavigation: 'Keyboard navigation',
        focusIndicators: 'Focus indicators',
        skipToContent: 'Skip to content',
        translating: 'Translating the page…',
        translated:
            'The page was translated. This is an enhancement, not a certified translation.',
        unavailable:
            'Sunbird translation is not configured. Widget labels still change.',
        failed: 'The page could not be translated. You can keep using the widget in this language.',
        disclaimer:
            'These tools enhance access. They do not make the website WCAG compliant.',
        nitaName: 'NITA-U',
        nitaCredit:
            'A programme of the National Information Technology Authority — Uganda',
        activeCount: '{count} active',
        searchLanguages: 'Search 30+ languages…',
        noMatchingLanguage: 'No matching language',
    },
    lug: {
        brand: 'UGACCESS',
        title: 'Okusobozesa',
        close: 'Ggalawo',
        open: 'Ggulawo ebyokukozesa',
        launcher: 'Okusobozesa',
        shortcut: 'Alt+Shift+A',
        profiles: 'Enkola',
        display: 'Okulabika',
        textSize: "Obunene bw'ennukuta",
        decreaseText: 'Kendeeza ennukuta',
        increaseText: 'Gaziya ennukuta',
        vision: 'Amalaso',
        reading: 'Okusoma',
        motion: 'Okutambula n’okulaga',
        language: 'Olulimi',
        voiceReader: 'Eddoboozi eryosoma',
        voiceEngine: 'Ensibuko y’eddoboozi',
        browserVoice: 'Eddoboozi lya browser',
        voiceAuto: 'Kyenkya (Sunbird, cloud, oluvannyuma browser)',
        voiceSunbird: 'Sunbird',
        voiceAzure: 'Azure Speech',
        voiceGoogle: 'Google Cloud',
        voiceBrowser: 'Amaloboozi ga browser',
        voiceMatchLanguage: 'Goberera olulimi lw’olupapula',
        readingSpeed: 'Obuwumu bw’okusoma',
        decreaseSpeed: 'Kendeeza obuwumu',
        increaseSpeed: 'Yongera obuwumu',
        readPage: 'Soma olupapula',
        stopReading: 'Leka okusoma',
        reset: 'Zawo',
        highContrast: 'Enjawulo eyaamaanyi',
        grayscale: 'Lang’i emu',
        invertColors: 'Kyusa lang’i',
        highlightLinks: 'Laga ennyungiro',
        largeCursor: 'Akasongeso akanene',
        reduceMotion: 'Kendeeza okwekyankalanya',
        textSpacing: "Obwawula bw'ennukuta",
        readingMode: 'Enkola y’okusoma',
        keyboardNavigation: 'Okutambula ne keyboard',
        focusIndicators: 'Laga gyoli',
        skipToContent: 'Buuka ku kirala',
        translating: 'Tukyusa olupapula…',
        translated:
            'Olupapula lukyusiddwa. Kino kyongera obusobozi, si kukyusa okukakasa.',
        unavailable:
            'Sunbird tennategekeddwa. Enyukuta ezikola ku kiyiiya zikyuka.',
        failed: 'Olupapula terukyuse. Osobola okusigala n’olulimi luno ku kiyiiya.',
        disclaimer:
            'Ebyokukozesa bino byongera obusobozi. Tebifuula omukutu gukola WCAG.',
        nitaName: 'NITA-U',
        nitaCredit:
            'Pulogulaamu ya National Information Technology Authority — Uganda',
        activeCount: '{count} ebikola',
        searchLanguages: 'Noonya mu nnimi 30+…',
        noMatchingLanguage: 'Tewali lulimi lufaanana',
    },
};

type Handlers = {
    toggle: (key: string) => void;
    setProfile: (slug: string) => void;
    changeFont: (delta: number) => void;
    setLanguage: (language: string) => void;
    setVoiceEngine: (engine: VoiceEngine) => void;
    setBrowserVoice: (voiceURI: string | null) => void;
    changeReadingRate: (delta: number) => void;
    setPosition?: (position: WidgetPosition) => void;
    reset: () => void;
    close: () => void;
    readPage: () => void;
};

type ToggleKey = keyof Omit<FeatureSettings, 'fontSize'>;

const groups: Array<{
    heading: string;
    keys: ToggleKey[];
}> = [
    {
        heading: 'vision',
        keys: [
            'highContrast',
            'grayscale',
            'invertColors',
            'highlightLinks',
            'largeCursor',
        ],
    },
    {
        heading: 'reading',
        keys: ['textSpacing', 'readingMode'],
    },
    {
        heading: 'motion',
        keys: [
            'reduceMotion',
            'keyboardNavigation',
            'focusIndicators',
            'skipToContent',
        ],
    },
];

export function mountWidget(
    state: InclusiveState,
    profiles: Profile[],
    handlers: Handlers,
): {
    root: HTMLDivElement;
    render: (next: InclusiveState) => void;
    destroy: () => void;
} {
    const host = document.createElement('div');
    host.id = 'inclusive-widget';
    const shadow = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = widgetCss;
    const wrap = document.createElement('div');
    wrap.className = `wrap ${state.position ?? state.options.position ?? 'bottom-right'}`;
    applyTheme(wrap, state.color ?? state.options.color);
    shadow.append(style, wrap);
    document.body.append(host);
    ensureAccessSkipLink(handlers);
    let rendered: InclusiveState | null = null;
    let voiceListVersion = 0;

    const onVoicesChanged = (): void => {
        voiceListVersion += 1;

        if (rendered?.open) {
            render(rendered);
        }
    };

    if (window.speechSynthesis) {
        window.speechSynthesis.addEventListener?.(
            'voiceschanged',
            onVoicesChanged,
        );
        window.speechSynthesis.onvoiceschanged = onVoicesChanged;
    }

    const render = (next: InclusiveState): void => {
        applyTheme(wrap, next.color ?? next.options.color);
        const persistPanel =
            Boolean(wrap.querySelector('#inclusive-panel')) && next.open;
        const t = copy[next.language] ?? copy.en;

        if (persistPanel && canUpdateReadingInPlace(rendered, next)) {
            syncReadingButton(wrap, next, t);
            rendered = next;

            return;
        }

        rendered = next;
        wrap.innerHTML = '';
        wrap.className = `wrap ${next.position ?? next.options.position ?? 'bottom-right'}`;

        const activeCount = countActiveFeatures(next);
        const button = document.createElement('button');
        button.className = 'button';
        button.type = 'button';
        button.setAttribute('aria-label', next.open ? t.close : t.open);
        button.setAttribute('aria-expanded', String(next.open));
        button.setAttribute('aria-controls', 'inclusive-panel');
        button.setAttribute('aria-keyshortcuts', 'Alt+Shift+A');
        button.title = `${next.open ? t.close : t.open} (${t.shortcut})`;
        button.innerHTML = `${next.open ? closeLauncherIcon : launcherIcon}<span class="button-label" aria-hidden="true">${t.launcher}</span>${activeCount > 0 && !next.open ? `<span class="button-badge" aria-hidden="true">${activeCount}</span>` : ''}`;
        const accessLink = document.getElementById('inclusive-access-link');

        if (accessLink) {
            accessLink.textContent = t.open;
        }
        button.addEventListener('click', () => {
            if (next.open) {
                handlers.close();
            } else {
                handlers.toggle('open');
            }
        });
        wrap.append(button);

        if (!next.open) {
            return;
        }

        const panel = document.createElement('div');
        panel.className = 'panel';
        panel.id = 'inclusive-panel';
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.setAttribute('aria-labelledby', 'inclusive-title');

        const header = document.createElement('div');
        header.className = 'header';
        const heading = document.createElement('div');
        heading.className = 'heading';

        const headerTop = document.createElement('div');
        headerTop.className = 'header-top';
        const brand = document.createElement('p');
        brand.className = 'brand';
        brand.textContent = t.brand;
        headerTop.append(brand);

        if (activeCount > 0) {
            const activePill = document.createElement('span');
            activePill.className = 'active-pill';
            activePill.textContent = (t.activeCount ?? '{count} active').replace(
                '{count}',
                String(activeCount),
            );
            headerTop.append(activePill);
        }

        const title = document.createElement('h2');
        title.className = 'title';
        title.id = 'inclusive-title';
        title.textContent = t.title;
        heading.append(headerTop, title);

        const close = document.createElement('button');
        close.className = 'close';
        close.type = 'button';
        close.setAttribute('aria-label', t.close);
        close.textContent = '×';
        close.addEventListener('click', () => handlers.close());
        header.append(heading, close);

        const body = document.createElement('div');
        body.className = 'body';

        body.append(section(t.language));
        const langCard = document.createElement('div');
        langCard.className = 'control-card select-card';
        const langInfo = document.createElement('div');
        langInfo.className = 'control-info';
        const langIcon = document.createElement('span');
        langIcon.className = 'control-icon';
        langIcon.setAttribute('aria-hidden', 'true');
        langIcon.innerHTML = globeIcon;
        const langLabel = document.createElement('span');
        langLabel.className = 'control-label';
        langLabel.textContent = t.language;
        langInfo.append(langIcon, langLabel);

        const languages =
            next.languages.length > 0 ? next.languages : bundledLanguages;
        const currentLangObj = languages.find((l) => l.code === next.language);
        const currentLangLabel = currentLangObj
            ? currentLangObj.label
            : next.language.toUpperCase();

        const langBadge = document.createElement('span');
        langBadge.className = 'lang-active-badge';
        langBadge.textContent = currentLangLabel;
        langInfo.append(langBadge);

        langCard.append(langInfo);

        const quickCodes = ['en', 'lug', 'swa', 'nyn', 'teo', 'ach'];
        const quickLanguages = quickCodes
            .map((code) => languages.find((l) => l.code === code))
            .filter((l): l is { code: string; label: string } => Boolean(l));

        if (quickLanguages.length > 0) {
            const chipsContainer = document.createElement('div');
            chipsContainer.className = 'lang-chips';
            quickLanguages.forEach((qlang) => {
                const chip = document.createElement('button');
                chip.type = 'button';
                chip.className = 'lang-chip';
                chip.setAttribute(
                    'aria-pressed',
                    String(next.language === qlang.code),
                );
                chip.title = qlang.label;
                chip.innerHTML = `<span class="lang-code-pill">${qlang.code.toUpperCase()}</span><span>${qlang.label}</span>`;
                chip.addEventListener('click', () => {
                    handlers.setLanguage(qlang.code);
                });
                chipsContainer.append(chip);
            });
            langCard.append(chipsContainer);
        }

        const searchWrap = document.createElement('div');
        searchWrap.className = 'lang-search-wrap';

        const searchIconSpan = document.createElement('span');
        searchIconSpan.className = 'lang-search-icon';
        searchIconSpan.setAttribute('aria-hidden', 'true');
        searchIconSpan.innerHTML = searchIcon;

        const searchInput = document.createElement('input');
        searchInput.type = 'search';
        searchInput.className = 'lang-search-input';
        searchInput.placeholder = t.searchLanguages ?? 'Search 30+ languages…';
        searchInput.setAttribute(
            'aria-label',
            t.searchLanguages ?? 'Search languages',
        );

        searchWrap.append(searchIconSpan, searchInput);
        langCard.append(searchWrap);

        const selectWrap = document.createElement('div');
        selectWrap.className = 'select-wrap';
        const select = document.createElement('select');
        select.setAttribute('aria-label', t.language);

        const populateOptions = (
            list: Array<{ code: string; label: string }>,
        ): void => {
            select.innerHTML = '';
            if (list.length === 0) {
                const emptyOption = document.createElement('option');
                emptyOption.disabled = true;
                emptyOption.textContent =
                    t.noMatchingLanguage ?? 'No matching language';
                select.append(emptyOption);
                return;
            }
            list.forEach((language) => {
                const option = document.createElement('option');
                option.value = language.code;
                option.textContent = language.label;
                option.selected = next.language === language.code;
                select.append(option);
            });
        };

        populateOptions(languages);

        searchInput.addEventListener('input', () => {
            const query = searchInput.value.trim().toLowerCase();
            if (!query) {
                populateOptions(languages);
                return;
            }
            const filtered = languages.filter(
                (l) =>
                    l.label.toLowerCase().includes(query) ||
                    l.code.toLowerCase().includes(query),
            );
            populateOptions(filtered);
        });

        searchInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                const query = searchInput.value.trim().toLowerCase();
                const match = languages.find(
                    (l) =>
                        l.label.toLowerCase().includes(query) ||
                        l.code.toLowerCase().includes(query),
                );
                if (match) {
                    handlers.setLanguage(match.code);
                }
            }
        });

        select.addEventListener('change', () =>
            handlers.setLanguage(select.value),
        );
        selectWrap.append(select);
        langCard.append(selectWrap);
        body.append(langCard);

        if (next.translationStatus !== 'idle' || next.language !== 'en') {
            const statusText = statusMessage(next, t);

            if (statusText) {
                const status = document.createElement('p');
                status.className =
                    next.translationStatus === 'failed'
                        ? 'status is-failed'
                        : 'status';
                status.setAttribute('aria-live', 'polite');
                status.textContent = statusText;
                body.append(status);
            }
        }

        body.append(section(t.profiles));
        const profileBox = document.createElement('div');
        profileBox.className = 'profiles';
        (profiles.length ? profiles : bundledProfiles).forEach((profile) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'chip';
            chip.setAttribute(
                'aria-pressed',
                String(next.profile === profile.slug),
            );

            const iconSpan = document.createElement('span');
            iconSpan.className = 'chip-icon';
            iconSpan.setAttribute('aria-hidden', 'true');
            iconSpan.innerHTML = profileIcon(profile.slug);

            const nameSpan = document.createElement('span');
            nameSpan.className = 'chip-name';
            nameSpan.textContent = profile.name;

            chip.append(iconSpan, nameSpan);
            chip.addEventListener('click', () =>
                handlers.setProfile(profile.slug),
            );
            profileBox.append(chip);
        });
        body.append(profileBox);

        body.append(section(t.display));
        const sizeCard = document.createElement('div');
        sizeCard.className = 'control-card size-card';
        const sizeRow = document.createElement('div');
        sizeRow.className = 'row';
        const sizeLeft = document.createElement('div');
        sizeLeft.className = 'control-info';
        const sizeIcon = document.createElement('span');
        sizeIcon.className = 'control-icon';
        sizeIcon.setAttribute('aria-hidden', 'true');
        sizeIcon.innerHTML = typeIcon;
        const sizeLabel = document.createElement('span');
        sizeLabel.id = 'inclusive-text-size';
        sizeLabel.className = 'control-label';
        sizeLabel.textContent = t.textSize;
        sizeLeft.append(sizeIcon, sizeLabel);

        const stepper = document.createElement('div');
        stepper.className = 'stepper';
        stepper.append(
            stepButton(t.decreaseText, -1, handlers.changeFont),
            strong(formatFontSize(next.settings.fontSize)),
            stepButton(t.increaseText, 1, handlers.changeFont),
        );
        sizeRow.append(sizeLeft, stepper);
        sizeCard.append(sizeRow);
        body.append(sizeCard);

        groups.forEach((group) => {
            body.append(section(t[group.heading] ?? group.heading));
            const cardsGrid = document.createElement('div');
            cardsGrid.className = 'cards-grid';
            group.keys.forEach((key) => {
                cardsGrid.append(
                    switchRow(
                        t[key] ?? key,
                        next.settings[key],
                        () => handlers.toggle(key),
                        key,
                    ),
                );
            });
            body.append(cardsGrid);
        });

        body.append(section(t.voiceReader));
        const voiceCard = document.createElement('div');
        voiceCard.className = 'control-card voice-card';
        const voiceStack = document.createElement('div');
        voiceStack.className = 'field-stack';
        voiceStack.dataset.voiceListVersion = String(voiceListVersion);

        const engineSelect = document.createElement('select');
        engineSelect.setAttribute('aria-label', t.voiceEngine);
        (
            [
                ['auto', t.voiceAuto],
                ['sunbird', t.voiceSunbird],
                ...(next.azureTtsAvailable
                    ? ([['azure', t.voiceAzure]] as Array<
                          [VoiceEngine, string]
                      >)
                    : []),
                ...(next.googleTtsAvailable
                    ? ([['google', t.voiceGoogle]] as Array<
                          [VoiceEngine, string]
                      >)
                    : []),
                ['browser', t.voiceBrowser],
            ] as Array<[VoiceEngine, string]>
        ).forEach(([value, label]) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = label;
            option.selected = next.voiceEngine === value;
            engineSelect.append(option);
        });
        engineSelect.addEventListener('change', () =>
            handlers.setVoiceEngine(engineSelect.value as VoiceEngine),
        );
        voiceStack.append(engineSelect);

        const browserVoices = listBrowserVoices(
            next.language,
            next.browserVoiceURI,
        );
        const showBrowserVoices =
            next.voiceEngine === 'browser' ||
            (next.voiceEngine === 'auto' && browserVoices.length > 0);

        if (showBrowserVoices) {
            const voiceSelect = document.createElement('select');
            voiceSelect.setAttribute('aria-label', t.browserVoice);

            const defaultOption = document.createElement('option');
            defaultOption.value = '';
            defaultOption.textContent = t.voiceMatchLanguage;
            defaultOption.selected = next.browserVoiceURI === null;
            voiceSelect.append(defaultOption);

            browserVoices.forEach((voice) => {
                const option = document.createElement('option');
                option.value = voice.voiceURI;
                option.textContent = `${voice.name} (${voice.lang})`;
                option.selected = next.browserVoiceURI === voice.voiceURI;
                voiceSelect.append(option);
            });

            voiceSelect.addEventListener('change', () =>
                handlers.setBrowserVoice(
                    voiceSelect.value === '' ? null : voiceSelect.value,
                ),
            );
            voiceStack.append(voiceSelect);
        }

        const speedRow = document.createElement('div');
        speedRow.className = 'row';
        const speedLeft = document.createElement('div');
        speedLeft.className = 'control-info';
        const speedIcon = document.createElement('span');
        speedIcon.className = 'control-icon';
        speedIcon.setAttribute('aria-hidden', 'true');
        speedIcon.innerHTML = speedIconSvg;
        const speedLabel = document.createElement('span');
        speedLabel.className = 'control-label';
        speedLabel.textContent = t.readingSpeed;
        speedLeft.append(speedIcon, speedLabel);

        const speedStepper = document.createElement('div');
        speedStepper.className = 'stepper';
        speedStepper.append(
            stepButton(t.decreaseSpeed, -0.1, handlers.changeReadingRate),
            strong(formatReadingRate(next.readingRate)),
            stepButton(t.increaseSpeed, 0.1, handlers.changeReadingRate),
        );
        speedRow.append(speedLeft, speedStepper);
        voiceStack.append(speedRow);

        voiceCard.append(voiceStack);
        body.append(voiceCard);

        const footer = document.createElement('div');
        footer.className = 'footer';
        const actions = document.createElement('div');
        actions.className = 'actions';
        const read = document.createElement('button');
        read.className = 'action';
        read.type = 'button';
        read.dataset.inclusiveRead = 'true';
        read.textContent = next.reading ? t.stopReading : t.readPage;
        read.setAttribute('aria-pressed', String(Boolean(next.reading)));
        read.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            handlers.readPage();
        });
        const reset = document.createElement('button');
        reset.className = 'action reset';
        reset.type = 'button';
        reset.textContent = t.reset;
        reset.addEventListener('click', () => handlers.reset());
        actions.append(read, reset);
        const note = document.createElement('p');
        note.className = 'disclaimer';
        note.textContent = t.disclaimer;
        footer.append(
            actions,
            note,
            nitaBrand(t, next.options.apiBaseUrl ?? ''),
        );

        panel.append(header, body, footer);
        wrap.append(panel);
        trapFocus(panel);

        if (!persistPanel) {
            close.focus();
        }
    };

    render(state);

    const onKey = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            handlers.close();

            return;
        }

        if (
            event.altKey &&
            event.shiftKey &&
            event.code === 'KeyA' &&
            !event.repeat &&
            !event.ctrlKey &&
            !event.metaKey
        ) {
            event.preventDefault();
            const expanded =
                wrap.querySelector('.button')?.getAttribute('aria-expanded') ===
                'true';

            if (expanded) {
                handlers.close();
            } else {
                handlers.toggle('open');
            }
        }
    };
    document.addEventListener('keydown', onKey);

    return {
        root: wrap,
        render,
        destroy: () => {
            document.removeEventListener('keydown', onKey);

            if (window.speechSynthesis) {
                window.speechSynthesis.removeEventListener?.(
                    'voiceschanged',
                    onVoicesChanged,
                );

                if (window.speechSynthesis.onvoiceschanged === onVoicesChanged) {
                    window.speechSynthesis.onvoiceschanged = null;
                }
            }

            document.getElementById('inclusive-access-link')?.remove();
            host.remove();
        },
    };
}

const launcherIcon = `<svg class="launcher-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="4.5" r="2.2" fill="currentColor" stroke="none"/><path d="M4 9a15 15 0 0 1 16 0"/><path d="M12 9v6"/><path d="M7.5 21l4.5-6 4.5 6"/></svg>`;
const closeLauncherIcon = `<svg class="launcher-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
const searchIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`;

function ensureAccessSkipLink(handlers: Handlers): void {
    if (document.getElementById('inclusive-access-link')) {
        return;
    }

    const link = document.createElement('a');
    link.id = 'inclusive-access-link';
    link.href = '#inclusive-widget';
    link.className = 'inclusive-skip-link';
    link.textContent = copy.en.open;
    link.addEventListener('click', (event) => {
        event.preventDefault();
        handlers.toggle('open');
    });

    const existingSkip =
        document.getElementById('inclusive-skip-link') ??
        document.querySelector('a[href="#main"], a[href="#content"]');

    if (existingSkip) {
        existingSkip.after(link);
    } else {
        document.body.prepend(link);
    }
}

function nitaBrand(
    t: Record<string, string>,
    apiBaseUrl: string,
): HTMLAnchorElement {
    const brand = document.createElement('a');
    brand.className = 'nita-brand';
    brand.href = 'https://www.nita.go.ug';
    brand.target = '_blank';
    brand.rel = 'noopener noreferrer';
    brand.setAttribute('aria-label', `${t.nitaName}. ${t.nitaCredit}`);

    const logo = document.createElement('img');
    logo.className = 'nita-logo';
    logo.src = nitaLogoSrc(apiBaseUrl);
    logo.alt = '';
    logo.decoding = 'async';
    logo.addEventListener('error', () => {
        logo.remove();
    });

    const credit = document.createElement('span');
    credit.className = 'nita-credit';
    credit.textContent = t.nitaCredit;

    brand.append(logo, credit);

    return brand;
}

function nitaLogoSrc(apiBaseUrl: string): string {
    const origin = apiBaseUrl.trim().replace(/\/$/, '');

    if (origin !== '') {
        return `${origin}/images/nita-logo.png`;
    }

    const script = document.querySelector<HTMLScriptElement>(
        'script[src*="inclusive.js"]',
    );

    if (script?.src) {
        return new URL('/images/nita-logo.png', script.src).href;
    }

    return '/images/nita-logo.png';
}

function section(label: string): HTMLDivElement {
    const heading = document.createElement('div');
    heading.className = 'section';
    heading.textContent = label;

    return heading;
}

function strong(text: string): HTMLElement {
    const value = document.createElement('strong');
    value.textContent = text;

    return value;
}

function stepButton(
    label: string,
    delta: number,
    changeFont: (delta: number) => void,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'action';
    button.type = 'button';
    button.dataset.font = String(delta);
    button.setAttribute('aria-label', label);
    button.textContent = delta < 0 ? '−' : '+';
    button.addEventListener('click', () => changeFont(delta));

    return button;
}

function switchRow(
    label: string,
    checked: boolean,
    onToggle: () => void,
    key?: string,
): HTMLLabelElement {
    const row = document.createElement('label');
    row.className = checked ? 'switch-row is-active' : 'switch-row';
    if (key) {
        row.dataset.feature = key;
    }

    const cardTop = document.createElement('div');
    cardTop.className = 'card-top';

    const iconSpan = document.createElement('span');
    iconSpan.className = 'card-icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    iconSpan.innerHTML = featureIcon(key ?? '');

    const control = document.createElement('span');
    control.className = 'switch';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = checked;
    input.setAttribute('role', 'switch');
    input.setAttribute('aria-checked', String(checked));
    input.addEventListener('change', onToggle);
    const track = document.createElement('span');
    track.className = 'track';
    control.append(input, track);

    cardTop.append(iconSpan, control);

    const text = document.createElement('span');
    text.className = 'card-label';
    text.textContent = label;

    row.append(cardTop, text);

    return row;
}

function countActiveFeatures(state: InclusiveState): number {
    let count = 0;
    if (state.settings.fontSize !== 0) {
        count += 1;
    }
    const keys: ToggleKey[] = [
        'highContrast',
        'grayscale',
        'invertColors',
        'highlightLinks',
        'largeCursor',
        'reduceMotion',
        'textSpacing',
        'readingMode',
        'keyboardNavigation',
        'focusIndicators',
        'skipToContent',
    ];
    for (const key of keys) {
        if (state.settings[key]) {
            count += 1;
        }
    }
    return count;
}

function featureIcon(key: string): string {
    switch (key) {
        case 'highContrast':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor"/></svg>`;
        case 'grayscale':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 3v18M12 7l7 5M12 12l7 5M12 17l4 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
        case 'invertColors':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="3" width="18" height="18" rx="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M3 3l18 18H3z" fill="currentColor"/></svg>`;
        case 'highlightLinks':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 15l6-6M11 7l2-2a4 4 0 0 1 5.66 5.66l-2 2a4 4 0 0 1-5.66 0M13 17l-2 2a4 4 0 0 1-5.66-5.66l2-2a4 4 0 0 1 5.66 0" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        case 'largeCursor':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 3l13 8-6 2-4 8L4 3z" fill="currentColor"/></svg>`;
        case 'textSpacing':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7V4h16v3M12 4v16M8 20h8M2 13h3M19 13h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
        case 'readingMode':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 6a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v14a2 2 0 0 0-2-2H5a3 3 0 0 0-3 3V6zm20 0a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3v14a2 2 0 0 1 2-2h7a3 3 0 0 1 3 3V6z" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
        case 'reduceMotion':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><line x1="10" y1="9" x2="10" y2="15" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><line x1="14" y1="9" x2="14" y2="15" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;
        case 'keyboardNavigation':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="2" y="6" width="20" height="12" rx="3" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="6" cy="10" r="1" fill="currentColor"/><circle cx="10" cy="10" r="1" fill="currentColor"/><circle cx="14" cy="10" r="1" fill="currentColor"/><circle cx="18" cy="10" r="1" fill="currentColor"/><line x1="7" y1="14" x2="17" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
        case 'focusIndicators':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
        case 'skipToContent':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 5h16M12 9v11M7 15l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        default:
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 8v4l3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    }
}

function profileIcon(slug: string): string {
    switch (slug) {
        case 'standard':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="8" cy="7" r="2.2" fill="currentColor"/><circle cx="16" cy="12" r="2.2" fill="currentColor"/><circle cx="10" cy="17" r="2.2" fill="currentColor"/></svg>`;
        case 'visual':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>`;
        case 'blind':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 11v2a9 9 0 0 0 18 0v-2M12 2v4M7 6a5 5 0 0 1 10 0" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
        case 'motor':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 12V6a1.5 1.5 0 0 1 3 0v5M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V5.5a1.5 1.5 0 0 1 3 0V11M6 12v3a6 6 0 0 0 12 0v-3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
        case 'cognitive':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2l2.4 5.6 5.6 2.4-5.6 2.4-2.4 5.6-2.4-5.6-5.6-2.4 5.6-2.4z" fill="currentColor"/></svg>`;
        case 'dyslexia':
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 19L9.5 5h1L16 19h-2.2l-1.4-3.6H7.6L6.2 19H4zm4.2-5.4h4.1L10.2 8.7h-.2l-2 4.9zM18 19h3M18 15h3M18 11h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>`;
        default:
            return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M4 21v-2a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v2" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
    }
}

const typeIcon = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 7V4h12v3M9 4v16M6 20h6M15 12V9h6v3M18 9v11M16 20h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
const globeIcon = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M2 12h20M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
const speedIconSvg = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

function formatFontSize(step: number): string {
    if (step > 0) {
        return `+${step}`;
    }

    return String(step);
}

function formatReadingRate(rate: number): string {
    return `${Math.round(rate * 100)}%`;
}

function statusMessage(
    next: InclusiveState,
    t: Record<string, string>,
): string | null {
    if (next.translationStatus === 'translating') {
        return t.translating;
    }

    if (next.translationStatus === 'translated') {
        return t.translated;
    }

    if (next.translationStatus === 'unavailable' && next.language !== 'en') {
        return t.unavailable;
    }

    if (next.translationStatus === 'failed') {
        return t.failed;
    }

    return null;
}

function canUpdateReadingInPlace(
    previous: InclusiveState | null,
    next: InclusiveState,
): boolean {
    return (
        previous !== null &&
        previous.open === next.open &&
        previous.language === next.language &&
        previous.profile === next.profile &&
        previous.translationStatus === next.translationStatus &&
        previous.settings === next.settings &&
        previous.voiceEngine === next.voiceEngine &&
        previous.browserVoiceURI === next.browserVoiceURI &&
        previous.readingRate === next.readingRate &&
        previous.azureTtsAvailable === next.azureTtsAvailable &&
        previous.googleTtsAvailable === next.googleTtsAvailable &&
        previous.reading !== next.reading
    );
}

function syncReadingButton(
    wrap: HTMLElement,
    next: InclusiveState,
    t: Record<string, string>,
): void {
    const read = wrap.querySelector<HTMLButtonElement>('[data-inclusive-read]');

    if (!read) {
        return;
    }

    read.textContent = next.reading ? t.stopReading : t.readPage;
    read.setAttribute('aria-pressed', String(Boolean(next.reading)));
}

function trapFocus(panel: HTMLElement): void {
    const focusable = (): HTMLElement[] =>
        Array.from(
            panel.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            ),
        );

    panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') {
            return;
        }

        const items = focusable();
        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            last?.focus();
            event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === last) {
            first?.focus();
            event.preventDefault();
        }
    });
}
